import React, { useRef, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Phone, Mail, Pen, Zap, Monitor, Check } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Highlighter from '../components/Highlighter';
import LocalBusinessSchema from '../components/LocalBusinessSchema';
import '../styles/Clockwork.css';
import '../styles/Home.css';

const Home = () => {
  const videoRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (video && !isMobile) {
      video.muted = true;
      video.play().catch(err => console.log('Autoplay prevented:', err));
    }
  }, [isMobile]);

  return (
    <div className="clockwork-app">
      <LocalBusinessSchema />
      <Helmet>
        <title>Xerowrite | The Highest-Performing Direct Mail Postcard</title>
        <meta name="description" content="The only direct mail postcard with variable handwritten-style fluorescent pink highlighter. Millions mailed. The highest response rates in the industry." />
        <link rel="canonical" href="https://xerowrite.com/" />
        
        {/* Open Graph Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://xerowrite.com/" />
        <meta property="og:title" content="Xerowrite | The Highest-Performing Direct Mail Postcard" />
        <meta property="og:description" content="The only direct mail postcard with variable handwritten-style fluorescent pink highlighter. Millions mailed. The highest response rates in the industry." />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Xerowrite | The Highest-Performing Direct Mail Postcard" />
        <meta name="twitter:description" content="The only direct mail postcard with variable handwritten-style fluorescent pink highlighter. Millions mailed." />
      </Helmet>
      <Header />
      
      <main>
        {/* HERO SECTION */}
        <section className="hero-section">
          <div className="container">
            <div className="hero-content">
              <h1 className="hero-title">
                The Highest-Performing Direct Mail Postcard
              </h1>
              <p className="hero-subtitle">
                Handwritten-style postcards with <Highlighter>fluorescent pink highlighter</Highlighter>. Exclusive to Xerowrite. Ordered today. At the post office tomorrow.
              </p>
              
              <div className="cta-group hero-cta">
                <Link to="/about" className="btn btn-primary">
                  Order Samples
                </Link>
                <a href="tel:8015097884" className="btn btn-secondary">
                  <Phone size={20} />
                  Call 801.509.7884
                </a>
              </div>
            </div>

            {/* VIDEO EMBED */}
            <div className="hero-video-container">
              <video
                ref={videoRef}
                className="hero-video"
                autoPlay
                loop
                muted
                playsInline
                aria-label="Handwritten-style direct mail postcard with fluorescent pink personalized message and phone number circle, exclusive to Xerowrite"
                title="Xerowrite Handwritten Direct Mail Postcard Demo"
              >
                <source 
                  src="https://customer-assets.emergentagent.com/job_print-showcase-18/artifacts/ghticdr4_Hero%20Loop.mp4" 
                  type="video/mp4" 
                />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </section>

        {/* THREE PILLARS SECTION */}
        <section className="section pillars-section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">Why Xerowrite Outperforms Every Other Direct Mail Format</h2>
            </div>
            
            <div className="pillars-grid">
              <div className="pillar-card">
                <div className="pillar-icon">
                  <Pen size={32} />
                </div>
                <h2 className="pillar-title">No One Else Has This</h2>
                <p className="pillar-text">
                  The fluorescent pink highlighter isn&apos;t just a design element, it&apos;s variable data. Your customer&apos;s name, their phone number, a specific price, a hard deadline, written in 100% realistic pink highlighter, exactly where you need their eye to go. No competitor offers this. It is exclusively Xerowrite.
                </p>
              </div>

              <div className="pillar-card">
                <div className="pillar-icon" style={{background: '#FF2D78', color: 'white', borderRadius: '50%', width: '64px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                  <Zap size={32} />
                </div>
                <h2 className="pillar-title">The Highest Response Rates in Direct Mail</h2>
                <p className="pillar-text">
                  Many millions of pieces mailed across the country, year after year. Our clients consistently see response rates that no standard postcard, printed mailer, or digital campaign can match. The numbers speak for themselves, and they&apos;re repeatable.
                </p>
              </div>

              <div className="pillar-card">
                <div className="pillar-icon">
                  <Check size={32} />
                </div>
                <h2 className="pillar-title">Ordered Today. At the Post Office Tomorrow.</h2>
                <p className="pillar-text">
                  Most suppliers need three to five days in production alone. We turn orders around in one business day. Whether you need maximum speed or a scheduled drop date, we work on your timeline, not ours.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ROI COMPARISON SECTION - NEW */}
        <section className="section">
          <div className="container-narrow" style={{textAlign: 'center'}}>
            <h2 className="section-title">Better Returns. Lower Cost. No Trade-Off.</h2>
            <p className="section-subtitle" style={{marginBottom: '2rem'}}>
              Competing handwritten mail services charge $3 to $8 per piece. Xerowrite delivers the same high response rates, often higher, at a fraction of that cost. When your price per piece drops and your response rates hold, your return on investment goes up. That&apos;s the only math that matters.
            </p>
            <Link to="/product" className="btn btn-primary">
              See Pricing
            </Link>
          </div>
        </section>

        {/* PROVEN RESULTS SECTION */}
        <section className="section section-gray social-proof-section">
          <div className="container-narrow">
            <div className="section-header" style={{textAlign: 'center'}}>
              <h2 className="section-title">Proven Results. Many Millions of Pieces Mailed Every Year.</h2>
              <p className="section-subtitle">
                The highest response rates in the market, consistently, across every industry we serve. No other direct mail format combines personalization, visual impact, and next-day turnaround at this price point. This is what we do, and we&apos;ve been doing it at scale for years.
              </p>
              <Link to="/about" className="btn btn-primary" style={{marginTop: '1.5rem'}}>
                Get a Free Sample
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Home;
